import { AppState, MapIcon, Node, Path, SelectionRef, Wall, Zone } from '../types';

export interface ArchitectClipboard {
  nodes: Node[];
  zones: Zone[];
  walls: Wall[];
  paths: Path[];
  icons: MapIcon[];
}

const clone = <T,>(value: T): T => JSON.parse(JSON.stringify(value));
const makeId = () => globalThis.crypto?.randomUUID?.() ?? Math.random().toString(36).slice(2, 11);

export const createArchitectClipboard = (state: AppState, selection: SelectionRef[]): ArchitectClipboard => {
  const selected = new Set(selection.map(item => `${item.kind}:${item.id}`));
  const paths = state.paths.filter(path => selected.has(`PATH:${path.id}`));
  const nodeIds = new Set(selection.filter(item => item.kind === 'NODE').map(item => item.id));
  paths.forEach(path => path.nodeIds?.forEach(id => { if (id) nodeIds.add(id); }));
  return clone({
    nodes: state.nodes.filter(node => nodeIds.has(node.id)),
    zones: state.zones.filter(zone => selected.has(`ZONE:${zone.id}`)),
    walls: state.walls.filter(wall => selected.has(`WALL:${wall.id}`)),
    paths,
    icons: state.icons.filter(icon => selected.has(`ICON:${icon.id}`))
  });
};

export const instantiateClipboard = (
  clipboard: ArchitectClipboard,
  offset: number,
  bounds: { width: number; height: number }
) => {
  const nodeIds = new Map(clipboard.nodes.map(node => [node.id, makeId()]));
  const zoneIds = new Map(clipboard.zones.map(zone => [zone.id, makeId()]));
  const wallIds = new Map(clipboard.walls.map(wall => [wall.id, makeId()]));
  const pathIds = new Map(clipboard.paths.map(path => [path.id, makeId()]));
  const iconIds = new Map(clipboard.icons.map(icon => [icon.id, makeId()]));

  const coordinates = [
    ...clipboard.nodes.map(node => ({ x: node.x, y: node.y })),
    ...clipboard.icons.map(icon => ({ x: icon.x, y: icon.y })),
    ...clipboard.walls.flatMap(wall => [{ x: wall.x1, y: wall.y1 }, { x: wall.x2, y: wall.y2 }]),
    ...clipboard.zones.flatMap(zone => zone.rects.flatMap(rect => [{ x: rect.x, y: rect.y }, { x: rect.x + rect.w, y: rect.y + rect.h }])),
    ...clipboard.paths.flatMap(path => path.points)
  ];
  const minX = coordinates.length ? Math.min(...coordinates.map(point => point.x)) : 0;
  const minY = coordinates.length ? Math.min(...coordinates.map(point => point.y)) : 0;
  const maxX = coordinates.length ? Math.max(...coordinates.map(point => point.x)) : 0;
  const maxY = coordinates.length ? Math.max(...coordinates.map(point => point.y)) : 0;
  const dx = Math.max(-minX, Math.min(offset, bounds.width - maxX));
  const dy = Math.max(-minY, Math.min(offset, bounds.height - maxY));

  const nodes = clipboard.nodes.map(node => ({
    ...clone(node), id: nodeIds.get(node.id)!, x: node.x + dx, y: node.y + dy,
    connections: node.connections.flatMap(id => nodeIds.get(id) ?? [])
  }));
  const zones = clipboard.zones.map(zone => ({
    ...clone(zone), id: zoneIds.get(zone.id)!, rects: zone.rects.map(rect => ({ ...rect, x: rect.x + dx, y: rect.y + dy })),
    entryPointIds: zone.entryPointIds?.flatMap(id => nodeIds.get(id) ?? [])
  }));
  const walls = clipboard.walls.map(wall => ({
    ...wall, id: wallIds.get(wall.id)!, x1: wall.x1 + dx, y1: wall.y1 + dy, x2: wall.x2 + dx, y2: wall.y2 + dy
  }));
  const paths = clipboard.paths.map(path => ({
    ...clone(path), id: pathIds.get(path.id)!, points: path.points.map(point => ({ x: point.x + dx, y: point.y + dy })),
    nodeIds: path.nodeIds?.map(id => id ? nodeIds.get(id) ?? null : null),
    generatedNodeIds: path.generatedNodeIds?.flatMap(id => nodeIds.get(id) ?? []),
    connections: path.connections?.flatMap(edge => {
      const fromId = nodeIds.get(edge.fromId); const toId = nodeIds.get(edge.toId);
      return fromId && toId ? [{ fromId, toId }] : [];
    })
  }));
  const icons = clipboard.icons.map(icon => ({ ...clone(icon), id: iconIds.get(icon.id)!, x: icon.x + dx, y: icon.y + dy }));
  const selection: SelectionRef[] = [
    ...nodes.map(node => ({ kind: 'NODE' as const, id: node.id })),
    ...zones.map(zone => ({ kind: 'ZONE' as const, id: zone.id })),
    ...walls.map(wall => ({ kind: 'WALL' as const, id: wall.id })),
    ...paths.map(path => ({ kind: 'PATH' as const, id: path.id })),
    ...icons.map(icon => ({ kind: 'ICON' as const, id: icon.id }))
  ];
  return { nodes, zones, walls, paths, icons, selection };
};
